import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { profileTutorialSeen, type Role, type ViewId } from '@aithora/shared';
import { useConsoleRole, useSession } from '../session/SessionContext';

/**
 * The "First time here" banner (design: tutShow). Andrew, 2026-10-19: show it only for a
 * person's first few sign-ins, and once they close it, it never comes back for them.
 * - Shown on a person's first WELCOME_SIGNINS (3) sign-ins (/auth/me signin_count), never after.
 * - "Not now" (the design's tutLater) hides it for this sign-in only: it comes back next time,
 *   while sign-ins remain. "Start" opens the guide and does the same.
 * - "Don't show again" and the X close it for good, remembered on the account (PATCH
 *   /auth/profile {tutorialSeen}; /auth/me tutorial_seen), so no browser or device brings it
 *   back. Browser storage is only the fast first read.
 * - On the last sign-in it says so, in the design's words: "Last reminder. It stays in Help
 *   after this." Its content stays in Help for good (HelpPage's FOR YOUR ROLE card).
 * Role rules from the design stay:
 * - Shown to Super Admin, Operator and Facility Admin; Care Staff, Family, Resident and
 *   Aithora Home get their own setup flows instead, and the Partner none (V7 T has no Partner).
 * - Facility Admin: only once the setup checklist is dismissed (the checklist carries
 *   the same guide while it is up).
 * - Not on Help or Atlas.
 */

/** Sign-ins the banner is shown on (the server's signin-count.ts WELCOME_SIGNINS). */
export const WELCOME_SIGNINS = 3;

interface Copy {
  title: string;
  note: string;
}

const COPY: Partial<Record<Role, Copy>> = {
  SUPER_ADMIN: { title: 'How we talk about our own numbers', note: 'Five screens on the honesty contract, the gate, and scope.' },
  OPERATOR: { title: 'How we talk about our own numbers', note: 'Five screens on the honesty contract, the gate, and scope.' },
  ADMIN: { title: 'Set up your building', note: 'Your setup checklist, plus how-to answers. Your building is already filled in.' },
};

/** Fired on window when the Facility Admin's checklist prefs change (detail: { dismissed, collapsed }). */
export const CHECKLIST_PREFS_EVENT = 'aithora:checklist-prefs';

/** The Guide each banner opens (design tutHref: Aithora Guide.dc.html?role=...). */
const GUIDE_FOR: Partial<Record<Role, string>> = { SUPER_ADMIN: '/guide?for=Operator', OPERATOR: '/guide?for=Operator', ADMIN: '/guide' };
export const guideFor = (role: Role | null): string => (role ? GUIDE_FOR[role] ?? '/guide' : '/guide');

/** Closed for good on this browser (the X or "Don't show again"). */
export const dismissedKey = (userId: string) => `aithora_tut_dismissed_v4_${userId}`;
/** "Not now" (or Start) for this sign-in: kept for the browser session only. */
export const notNowKey = (userId: string) => `aithora_tut_not_now_v4_${userId}`;

function read(store: 'local' | 'session', key: string): string | null {
  try {
    return (store === 'local' ? window.localStorage : window.sessionStorage).getItem(key);
  } catch {
    return null;
  }
}
function write(store: 'local' | 'session', key: string, v: string) {
  try {
    (store === 'local' ? window.localStorage : window.sessionStorage).setItem(key, v);
  } catch {
    /* storage off: the account still remembers it */
  }
}

export function bannerCopyFor(role: Role | null): Copy | null {
  return role ? COPY[role] ?? null : null;
}

/** Whether this person still gets the banner: one of their first sign-ins, never closed. */
export function welcomeDue(userId: string | null, closedOnAccount: boolean, signinCount: number): boolean {
  if (!userId || closedOnAccount) return false;
  if (read('local', dismissedKey(userId)) === '1') return false;
  if (read('session', notNowKey(userId)) === '1') return false;
  return signinCount >= 1 && signinCount <= WELCOME_SIGNINS;
}

export function FirstTimeBanner({ role, view, checklistVisible }: { role: Role | null; view: ViewId | null; checklistVisible: boolean }) {
  const { session, client } = useSession();
  const { realRole } = useConsoleRole();
  const userId = session?.user.id ?? null;
  // While a Super Admin views as another role, nothing is written to the account.
  const viewing = !!role && !!realRole && role !== realRole;
  const copy = bannerCopyFor(role);
  const signins = session?.user.signinCount ?? 0;
  const closedOnAccount = session?.user.tutorialSeen === true;
  const [open, setOpen] = useState(() => welcomeDue(userId, closedOnAccount, signins));
  // The account's answer (/auth/me) can arrive after the first paint: follow it.
  useEffect(() => {
    setOpen(welcomeDue(userId, closedOnAccount, signins));
  }, [userId, closedOnAccount, signins]);
  const shown = !!userId && !!role && !!copy && open && view !== 'help' && view !== 'atlas' && !(role === 'ADMIN' && checklistVisible);

  if (!shown || !role || !copy || !userId) return null;

  const notNow = () => {
    write('session', notNowKey(userId), '1');
    setOpen(false);
  };
  const close = () => {
    write('local', dismissedKey(userId), '1');
    if (!viewing) profileTutorialSeen(client, { body: { tutorialSeen: true } }).catch(() => undefined);
    setOpen(false);
  };
  const last = signins >= WELCOME_SIGNINS;

  return (
    <div className="tutbanner" role="region" aria-label="First time here">
      <span className="tutbanner__tag" title="It stays in Help either way, and Atlas can answer from it.">
        FIRST TIME HERE
      </span>
      <span className="tutbanner__text">
        <span className="tutbanner__title">{copy.title}</span>
        <span className="tutbanner__note">{copy.note}</span>
      </span>
      <span className="tutbanner__actions">
        {last && <span className="tutbanner__last">Last reminder. It stays in Help after this.</span>}
        <Link to={guideFor(role)} className="tutbanner__start" onClick={notNow}>
          Start
        </Link>
        <button type="button" className="tutbanner__later" onClick={notNow}>
          Not now
        </button>
        <button type="button" className="tutbanner__never" onClick={close}>
          Don't show again
        </button>
        <button type="button" className="tutbanner__close" aria-label="Dismiss" title="Dismiss" onClick={close}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </span>
    </div>
  );
}
